import React, { JSX, useEffect, useRef, useState } from 'react';
import { useFilter } from 'components/context/FilterContext';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';
import ControlToggles from '../shared/ControlToggles';
import { useRouter } from 'next/router';
import LocationsSearchResults from './LocationsSearchResults';
import LocationFilters from './filters/LocationFilters';
import LocationMap from './LocationMap';
import ResultsControls from '../shared/ResultsControls';
import {
  LocationFiltersType,
  LocationDefaultsType,
  LocationFilterPanelsType,
  AddFilterType,
  ResetFilterToDefaultType,
  RemoveFilterType,
  UpdateQueryType,
  SetActiveFilterPanelType,
  UpdateFilterType,
  ToggleMobileControlsType,
  SetControlPanelType,
  OnQueryInputChangeType,
  LocationFilterOptionsType,
  FilterPanelType,
  NonDefaultFiltersType,
  UpdateSortType,
  DataFacet,
  FacetQueryType,
  FacetType,
  FacetValue,
} from 'components/types/FilterTypes';
import { LocationFields } from 'components/types/MapTypes';
import Axios from 'axios';
import { getLocationsSearchSrc, getSearchWidget } from 'lib/util';
import HeroComponent from 'components/common/HeroComponent';
import SearchBar from '../shared/filters/SearchBar';
import Constants from 'assets/utils/constant';
import { convertDistance } from 'assets/utils/convertDistance';

type LocationSearchState = {
  activeFilterPanel: string;
  apiError: boolean;
  query: string;
  queryInputValue: string;
  page: number;
  pageSize: number;
  locationsPageSize: number;
  paginationSize: number;
  results: Array<LocationFields>;
  count: number;
  sort: string;
  facets: Array<FacetType>;
  filters: LocationFilterOptionsType;
  selectedFilters: LocationFiltersType;
  selectedFacets: any;
  providerFilterPanels: Array<FilterPanelType>;
  locationFilterPanels: Array<FilterPanelType>;
  nonDefaultSelectedFilters: NonDefaultFiltersType;
  lastViewportSize: string;
  mobileControlsOpen: boolean;
  desktopControlsOpen: boolean;
  controlPanel: string;
  loading: boolean;
  viewMode: string;
  geolocating: boolean;
  latitude: number | string;
  location: string;
  longitude: number | string;
};

type NonDefaultParams = {
  [key: string]: string | Array<string> | number | undefined;
};

type LatLng = {
  lat: number;
  lng: number;
};

type NormalizedAddress = {
  city?: string;
  state?: string;
  zip?: string;
  label?: string;
};

const filterDefaults: LocationDefaultsType = {
  distance: '10',
  languages: [],
  locationtypes: [],
  PublishToLocal: 'true',
  query: '',
  sort: 'BEST_MATCH',
  specialty: [],
  page: 1,
};

const pageSizeOptions = [10, 20];

const initialState: LocationSearchState = {
  activeFilterPanel: '',
  apiError: false,
  query: filterDefaults.query,
  queryInputValue: filterDefaults.query,
  page: filterDefaults.page,
  pageSize: pageSizeOptions[0],
  locationsPageSize: 100,
  paginationSize: 10,
  results: [],
  count: 0,
  sort: filterDefaults.sort,
  facets: [],
  filters: {
    distance: '10',
    languages: [],
    locationtypes: [],
    PublishToLocal: 'true',
  },
  selectedFilters: {
    distance: '10',
    languages: Array<string>(),
    latitude: '',
    location: '',
    longitude: '',
    locationtypes: Array<string>(),
    PublishToLocal: 'true',
  },
  selectedFacets: {
    locationtypes: Array<string>(),
  },
  providerFilterPanels: [
    {
      name: 'Search',
      label: 'Enter name, specialty, or condition',
      icon: 'search',
      id: 'search',
      active: false,
    },
    {
      name: 'Location',
      label: 'City or ZIP',
      id: 'location',
      active: false,
    },
    {
      name: 'Availability',
      id: 'availability',
      active: false,
    },
    {
      name: 'Specialty',
      id: 'specialty',
      active: false,
    },
    {
      name: 'Gender',
      id: 'gender',
      active: false,
    },
    {
      name: 'Languages Spoken',
      id: 'languages',
      active: false,
    },
    {
      name: 'Office',
      id: 'office',
      active: false,
    },
  ],
  locationFilterPanels: [
    {
      name: 'Search',
      label: 'name, city or address',
      icon: 'search',
      id: 'search',
      active: false,
    },
    {
      name: 'Location',
      label: 'City or ZIP',
      id: 'location',
      active: false,
    },
    {
      name: 'Facility Type',
      id: 'locationtypes',
      active: false,
    },
  ],
  nonDefaultSelectedFilters: {},
  lastViewportSize: getOsfViewportSize(),
  mobileControlsOpen: false,
  desktopControlsOpen: false,
  controlPanel:
    getOsfViewportSize() === 'lg' || getOsfViewportSize() === 'xl'
      ? 'filters'
      : '',
  loading: false,
  viewMode: 'list',
  geolocating: false,
  latitude: '',
  location: '',
  longitude: '',
};

const LocationSearch = (): JSX.Element => {
  const [searchState, setSearchState] = useState({ ...initialState });
  const [allFacets, setAllFacets] = useState<Array<FacetType>>([]);
  const currentReq = useRef(0);
  const { setFilterState } = useFilter();
  const searchType: string = 'locations';
  const [isMapLoaded] = useState(true);
  const router = useRouter();
  const { query: routerQuery } = router;
  const lowercaseQuery = Object.fromEntries(
    Object.entries(routerQuery).map(([key, value]) => [
      key.toLowerCase(),
      value,
    ])
  );
  const mapRef = useRef('');
  const mapResultsRef = useRef(false);

  const { query } = searchState;

  let selectedFacetsCount = Object.values(searchState.selectedFacets).flat()
    .length;

  const addFilter: AddFilterType = (
    filter: string,
    item: string,
    facet: FacetValue
  ) => {
    if (facet) {
      const updatedFacet = localStorage.getItem(filter)
        ? [...JSON.parse(localStorage.getItem(filter) as string), facet]
        : [facet];
      localStorage.setItem(filter, JSON.stringify(updatedFacet));
    }
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      selectedFilters: {
        ...prevFields.selectedFilters,
        [filter]: [
          ...Object.values(
            prevFields.selectedFilters[filter as LocationFilterPanelsType]
          ),
          item,
        ],
      },
      selectedFacets: {
        ...prevFields.selectedFacets,
        [filter]: [
          ...Object.values(
            prevFields.selectedFacets[filter as LocationFilterPanelsType]
          ),
          facet,
        ],
      },
    }));
  };

  const clearAllFilters = () => {
    setSearchState(prevFields => ({
      ...prevFields,
      query: '',
      queryInputValue: '',
      page: filterDefaults.page,
      sort: filterDefaults.sort,
      selectedFilters: {
        distance: filterDefaults.distance,
        languages: filterDefaults.languages,
        latitude: '',
        location: '',
        longitude: '',
        specialty: filterDefaults.specialty,
        locationtypes: filterDefaults.locationtypes,
        PublishToLocal: filterDefaults.PublishToLocal,
        location_id: '',
      },
      selectedFacets: {
        languages: filterDefaults.languages,
        locationtypes: filterDefaults.locationtypes,
      },
    }));
    Object.keys(searchState.selectedFacets).forEach(facet => {
      if (localStorage.getItem(facet)) {
        localStorage.removeItem(facet);
      }
    });
  };

  const resetFilterToDefault: ResetFilterToDefaultType = (filter: string) => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      selectedFilters: {
        ...prevFields.selectedFilters,
        [filter]: filterDefaults[filter as keyof LocationDefaultsType]
          ? filterDefaults[filter as keyof LocationDefaultsType]
          : [],
      },
      selectedFacets: {
        ...prevFields.selectedFacets,
        [filter]: filterDefaults[filter as keyof LocationDefaultsType]
          ? filterDefaults[filter as keyof LocationDefaultsType]
          : [],
      },
    }));
  };

  const removeFilter: RemoveFilterType = (
    filter: string,
    facetName: string
  ) => {
    if (localStorage.getItem(filter)) {
      const updatedFacet = [
        ...JSON.parse(localStorage.getItem(filter) as string),
      ].filter(facet => facet.text !== facetName);
      updatedFacet.length == 0
        ? localStorage.removeItem(filter)
        : localStorage.setItem(filter, JSON.stringify(updatedFacet));
    }
    if (
      Array.isArray(filterDefaults[filter as keyof LocationDefaultsType]) &&
      typeof filterDefaults[filter as keyof LocationDefaultsType] !==
        'string' &&
      Object.values(
        searchState.selectedFilters[filter as LocationFilterPanelsType]
      ).length > 1
    ) {
      setSearchState(prevFields => ({
        ...prevFields,
        page: filterDefaults.page,
        selectedFilters: {
          ...prevFields.selectedFilters,
          [filter]: Object.values(
            prevFields.selectedFilters[filter as LocationFilterPanelsType]
          ).filter((e: string) => e !== facetName),
        },
        selectedFacets: {
          ...prevFields.selectedFacets,
          [filter]: Object.values(
            prevFields.selectedFacets[filter as LocationFilterPanelsType]
          ).filter(
            (selectedFacet: { text: string }) =>
              selectedFacet.text !== facetName
          ),
        },
      }));
    } else {
      resetFilterToDefault(filter);
    }
  };

  const replaceFilter = (filter: string, arr: Array<string>) => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      selectedFilters: {
        ...prevFields.selectedFilters,
        [filter]: arr,
      },
    }));
  };

  const replaceLocationFilters = (
    lat: number,
    lng: number,
    location: string
  ) => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      sort: '',
      selectedFilters: {
        ...prevFields.selectedFilters,
        latitude: lat,
        location: location,
        longitude: lng,
        distance: prevFields.selectedFilters.distance
          ? prevFields.selectedFilters.distance
          : '10',
      },
    }));
  };

  const removeLocationFilters = () => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      sort: filterDefaults.sort,
      selectedFilters: {
        ...prevFields.selectedFilters,
        distance: filterDefaults.distance,
        latitude: '',
        location: '',
        longitude: '',
      },
    }));
  };

  const updateQuery: UpdateQueryType = (str: string) => {
    setSearchState(state => ({
      ...state,
      activeFilterPanel: '',
      page: filterDefaults.page,
      query: str,
      queryInputValue: str,
      locationFilterPanels: state.locationFilterPanels.map(filterPanel =>
        filterPanel.id === 'search'
          ? { ...filterPanel, active: false }
          : { ...filterPanel }
      ),
    }));
  };

  const setActiveFilterPanel: SetActiveFilterPanelType = (id: string) => {
    const closingSearchFilter =
      searchState.activeFilterPanel === 'search' && id === '';

    setSearchState(prevFields => ({
      ...prevFields,
      activeFilterPanel: id,
      locationFilterPanels: searchState.locationFilterPanels.map(filterPanel =>
        filterPanel.id === id
          ? { ...filterPanel, active: true }
          : { ...filterPanel, active: false }
      ),
    }));

    closingSearchFilter && updateQuery(searchState.queryInputValue);
  };

  const openMobileControls = (control: string) => {
    if (!(getOsfViewportSize() === 'xl' || getOsfViewportSize() === 'xxl')) {
      document.body.classList.add('mobile-controls-open');

      setSearchState(prevFields => ({
        ...prevFields,
        controlPanel: control,
        mobileControlsOpen: true,
      }));
    }
  };

  const resetElementFocusMobile = (
    filtersAreOpen: boolean,
    onLoad: boolean
  ) => {
    let filtersClose = document.querySelector(
      '.mobile-filter-header .mobile-close-btn'
    ) as HTMLElement;
    let filtersOpen = document.querySelector(
      '.results-controls .mobile-filters-toggle'
    ) as HTMLElement;
    if (!(getOsfViewportSize() === 'lg' || getOsfViewportSize() === 'xl')) {
      if (filtersAreOpen) {
        filtersClose && filtersClose.focus(); //focus on the close button if filters are open
      } else {
        filtersOpen && filtersOpen.focus(); //focus on the open button if filters are closed
      }
      if (onLoad) {
        // for accessibility purposes, we need to move the results container element to the top of the DOM so it is read in the same order as it is visually
        let parent = document.querySelector('main');
        let titleBar = document.querySelector('.title-bar');
        let resultsContainer = document.querySelector(
          '.search-page-controls .results-controls'
        );
        resultsContainer && parent?.insertBefore(resultsContainer, titleBar);
      }
    }
  };

  const updateFilter: UpdateFilterType = (
    filter: string,
    item: string,
    facet: FacetValue
  ) => {
    setSearchState(prevFields => ({
      ...prevFields,
      page: filterDefaults.page,
      selectedFilters: {
        ...prevFields.selectedFilters,
        [filter]:
          filterDefaults[filter as keyof LocationDefaultsType] &&
          !Array.isArray(filterDefaults[filter as keyof LocationDefaultsType])
            ? item
            : [item],
      },
      selectedFacets: [...prevFields.selectedFacets, facet],
      locationFilterPanels: prevFields.locationFilterPanels.map(filterPanel => {
        return { ...filterPanel, active: false };
      }),
    }));
  };

  const toggleMobileControls: ToggleMobileControlsType = (control: string) => {
    const { controlPanel, mobileControlsOpen } = searchState;
    if (mobileControlsOpen) {
      if (controlPanel === control) {
        closeMobileControls();
      } else {
        setSearchState(prevFields => ({
          ...prevFields,
          controlPanel: control,
        }));
      }
    } else {
      openMobileControls(control);
    }
    setTimeout(() => {
      resetElementFocusMobile(mobileControlsOpen, false);
    }, 100);
  };

  const setControlPanel: SetControlPanelType = (control: string) => {
    const { lastViewportSize } = searchState;

    if (!(lastViewportSize === 'lg' || lastViewportSize === 'xl')) {
      toggleMobileControls(control);
    } else {
      document.body.classList.add('search-controls-open');
      setSearchState((prevState: LocationSearchState) => ({
        ...prevState,
        controlPanel: control,
        desktopControlsOpen: true,
      }));
    }
  };

  const onQueryInputChange: OnQueryInputChangeType = (value: string) => {
    setSearchState(prevFields => ({
      ...prevFields,
      queryInputValue: value,
    }));
  };

  const setLatLng = (latLng: LatLng, zip?: string) => {
    setFilterState(prevState => ({
      ...prevState,
      userLocation: {
        ...prevState.userLocation,
        latitude: latLng.lat,
        longitude: latLng.lng,
      },
    }));

    setSearchState(prevState => ({
      ...prevState,
      selectedFilters: {
        ...prevState.selectedFilters,
        latitude: latLng.lat,
        longitude: latLng.lng,
        distance: prevState.selectedFilters.distance
          ? prevState.selectedFilters.distance
          : '10',
      },
    }));

    if (!zip) {
      reverseGeocode(latLng);
    }
  };

  const buildAddress = (address: NormalizedAddress, latLng?: LatLng) => {
    const locationString =
      address.label ||
      `${address.city ? `${address.city}, ` : ''}${address.state ? `${address.state} ` : ''}${address.zip ?? ''}`.trim();

    if (latLng) {
      setFilterState(prevState => ({
        ...prevState,
        userLocation: {
          ...prevState.userLocation,
          location: locationString,
          latitude: latLng.lat,
          longitude: latLng.lng,
        },
      }));
    } else {
      setFilterState(prevState => ({
        ...prevState,
        userLocation: {
          ...prevState.userLocation,
          location: locationString,
        },
      }));
    }

    setSearchState(prevFields => ({
      ...prevFields,
      selectedFilters: {
        ...prevFields.selectedFilters,
        location: locationString,
        latitude: latLng?.lat || '',
        longitude: latLng?.lng || '',
      },
    }));

    if (latLng) {
      replaceLocationFilters(latLng.lat, latLng.lng, locationString);
    }
  };

  const reverseGeocode = async (latLng: LatLng, setAddress?: Function) => {
    const subscriptionKey = process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;
    if (!subscriptionKey) {
      console.error('Missing environment variable.');
      setSearchState(prevState => ({
        ...prevState,
        geolocating: false,
      }));
      setFilterState(prevState => ({
        ...prevState,
        userLocation: {
          ...prevState.userLocation,
          geolocating: false,
        },
      }));
      return;
    }

    try {
      const response = await Axios.get(
        'https://atlas.microsoft.com/reverseGeocode',
        {
          params: {
            'api-version': '2026-01-01',
            coordinates: `${latLng.lng},${latLng.lat}`,
            'subscription-key': subscriptionKey,
            language: 'en-US',
          },
        }
      );

      const result = response.data;

      const address: NormalizedAddress = {
        city:
          result?.features?.[0]?.properties?.address?.locality ||
          result?.features?.[0]?.properties?.address?.municipality,
        state:
          result?.features?.[0]?.properties?.address?.adminDistricts?.[0]
            ?.name ||
          result?.features?.[0]?.properties?.address?.countrySubdivision,
        zip: result?.features?.[0]?.properties?.address?.postalCode,
      };

      setAddress && setAddress(address);

      setSearchState(prevState => ({
        ...prevState,
        geolocating: false,
      }));

      setFilterState(prevState => ({
        ...prevState,
        userLocation: {
          ...prevState.userLocation,
          geolocating: false,
        },
      }));

      buildAddress(address, latLng);
    } catch (error) {
      console.log('Azure reverse geocoding failed:', error);

      setSearchState(prevState => ({
        ...prevState,
        geolocating: false,
      }));

      setFilterState(prevState => ({
        ...prevState,
        userLocation: {
          ...prevState.userLocation,
          geolocating: false,
        },
      }));
    }
  };

  const geolocate = (setAddress?: Function) => {
    setSearchState(prevFields => ({
      ...prevFields,
      geolocating: true,
    }));
    setFilterState(prevState => ({
      ...prevState,
      userLocation: {
        ...prevState.userLocation,
        geolocating: true,
      },
    }));

    navigator.geolocation.getCurrentPosition(
      position => {
        if (
          position &&
          position.coords &&
          position.coords.latitude &&
          position.coords.longitude
        ) {
          const latLng: LatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          reverseGeocode(latLng, setAddress);
        }
      },
      err => {
        setFilterState(prevState => ({
          ...prevState,
          userLocation: {
            ...prevState.userLocation,
            geolocating: false,
          },
        }));
        setSearchState(prevState => ({
          ...prevState,
          geolocating: false,
        }));
        console.log('Geocoding failed with ' + err);
      }
    );
  };

  const getMoreFacets = async (facet: FacetType, searchQuery?: {}) => {
    let facetResponse = null;
    let afterId = facet.value[facet.value.length - 1].id;
    const fullFacetList = facet.value;
    let moreFacets = true;

    while (moreFacets == true) {
      try {
        moreFacets = false;
        if (searchQuery) {
          const searchReq = {
            widget: {
              items: [
                {
                  rfk_id: getSearchWidget(),
                  entity: 'location',
                  search: {
                    ...searchQuery,
                    facet: {
                      all: false,
                      max: 100,
                      types: [
                        {
                          name: facet.name,
                          sort: {
                            name: 'text',
                            order: 'asc',
                          },
                          after: afterId,
                        },
                      ],
                    },
                  },
                },
              ],
            },
          };
          facetResponse = await Axios.post('/api/discovery', searchReq);
        } else {
          const searchReq = {
            widget: {
              items: [
                {
                  rfk_id: getSearchWidget(),
                  entity: 'location',
                  search: {
                    facet: {
                      all: false,
                      max: 100,
                      types: [
                        {
                          name: facet.name,
                          sort: {
                            name: 'text',
                            order: 'asc',
                          },
                          after: afterId,
                        },
                      ],
                    },
                  },
                },
              ],
            },
          };
          facetResponse = await Axios.post('/api/discovery', searchReq);
        }

        const facets = facetResponse.data.widgets[0].facet.find(
          (facet: FacetType) => facet.name == facet.name
        ).value;
        fullFacetList.push(...facets);

        if (facets.length < 100) {
          moreFacets = false;
        } else {
          afterId = facets[facets.length - 1].id;
        }
        allFacets.map((f: FacetType) => {
          if (f.name == facet.name) {
            f.value = fullFacetList;
          }
        });
      } catch (error) {
        console.log('Failed to fetch facets. Error: ' + error);
      }
    }
    return allFacets;
  };

  const getAllFacets = async () => {
    let response;
    try {
      const searchReq = {
        widget: {
          items: [
            {
              rfk_id: getSearchWidget(),
              entity: 'location',
              search: {
                facet: {
                  all: true,
                  max: 100,
                  types: [
                    {
                      name: 'locationtypes', // Only this facet will be sorted
                      sort: {
                        name: 'text',
                        order: 'asc',
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
      };
      response = await Axios.post('/api/discovery', searchReq);
      setAllFacets(response.data.widgets[0].facet);
    } catch (error) {
      console.log('Failed to fetch facets. Error: ' + error);
    }
  };

  const search = async () => {
    mapResultsRef.current = false;
    const reqNum = ++currentReq.current;
    const { filters, selectedFilters } = searchState;

    let nonDefaultSelectedFilters: { [key: string]: string | Array<string> } =
      Object.keys(selectedFilters).reduce((nonDefaultSelectedFilters, key) => {
        if (
          selectedFilters[key as keyof LocationFiltersType] ===
            filterDefaults[key as keyof LocationDefaultsType] ||
          selectedFilters[key as keyof LocationFiltersType] === null ||
          selectedFilters[key as keyof LocationFiltersType] === '' ||
          JSON.stringify(selectedFilters[key as keyof LocationFiltersType]) ===
            JSON.stringify([])
        ) {
          return nonDefaultSelectedFilters;
        }

        return {
          ...nonDefaultSelectedFilters,
          [key]: Array.isArray(
            selectedFilters[key as keyof LocationFiltersType]
          )
            ? selectedFilters[key as LocationFilterPanelsType].map(
                (val: string | Array<string> | boolean) => val.toString()
              )
            : selectedFilters[key as keyof LocationFiltersType],
        };
      }, {});

    setSearchState(prevState => ({
      ...prevState,
      loading: true,
      nonDefaultSelectedFilters,
    }));

    delete nonDefaultSelectedFilters['locationtypes'];
    if (selectedFilters.locationtypes.length > 0) {
      nonDefaultSelectedFilters['locationtypes'] =
        selectedFilters.locationtypes;
    }

    delete nonDefaultSelectedFilters['PublishToLocal'];
    if (selectedFilters.PublishToLocal.length > 0) {
      nonDefaultSelectedFilters['PublishToLocal'] =
        selectedFilters.PublishToLocal;
    }

    try {
      const nonPublishFilters = Object.keys(nonDefaultSelectedFilters).filter(
        key => key !== 'PublishToLocal' && key !== 'distance'
      );

      let typesArray: Array<FacetQueryType> = [];

      if (nonPublishFilters.length > 0) {
        Object.keys(searchState.selectedFacets).forEach(key => {
          if (
            searchState.selectedFacets[key].length > 0 ||
            Object.keys(searchState.selectedFacets[key]).length > 0
          ) {
            if (key == 'locationtypes') {
              typesArray.push({
                name: key,
                max: 100,
                filter: {
                  type: 'or',
                  values: Array.isArray(searchState.selectedFacets[key])
                    ? searchState.selectedFacets[key].map(
                        (facet: { id: any }) => facet.id
                      )
                    : [searchState.selectedFacets[key].id],
                },
                sort: {
                  name: 'text',
                  order: 'asc',
                },
              });
            } else {
              typesArray.push({
                name: key,
                max: 100,
                filter: {
                  type: 'or',
                  values: Array.isArray(searchState.selectedFacets[key])
                    ? searchState.selectedFacets[key].map(
                        (facet: { id: any }) => facet.id
                      )
                    : [searchState.selectedFacets[key].id],
                },
              });
            }
          }

          if (
            !Object.keys(nonDefaultSelectedFilters).includes('locationtypes')
          ) {
            typesArray.push({
              name: 'locationtypes',
              max: 100,
              sort: {
                name: 'text',
                order: 'asc',
              },
            });
          }
        });
      }

      const initialSearch: any = {
        content: {},
        limit:
          mapRef.current === 'map'
            ? searchState.locationsPageSize
            : searchState.pageSize,
        offset: (searchState.page - 1) * searchState.pageSize,
        facet: {
          all: true,
          max: 100,
          types: [
            {
              name: 'locationtypes',
              sort: {
                name: 'text',
                order: 'asc',
              },
            },
          ],
        },
      };

      let defaultSearchBody: any = {
        content: {},
        limit:
          mapRef.current === 'map'
            ? searchState.locationsPageSize
            : searchState.pageSize,
        offset: (searchState.page - 1) * searchState.pageSize,
        facet: {
          all: true,
          max: 100,
          types: [
            {
              name: 'locationtypes',
              sort: {
                name: 'text',
                order: 'asc',
              },
            },
          ],
        },
        filter: {
          type: 'and',
          filters: [
            {
              type: 'eq',
              name: 'publishtolocal',
              value: 'true',
            },
          ],
        },
        rule: {
          boost: [
            {
              weight: 2,
              filter: {
                type: 'exists',
                name: 'image',
              },
            },
          ],
        },
      };

      if (searchState.selectedFilters.location) {
        defaultSearchBody = {
          ...defaultSearchBody,
          filter: {
            type: 'and',
            filters: [
              {
                type: 'eq',
                name: 'publishtolocal',
                value: 'true',
              },
              {
                type: 'geoDistance',
                name: 'coordinate',
                distance: `${convertDistance(searchState.selectedFilters.distance)}km`,
                lat: parseFloat(searchState.selectedFilters.latitude as string),
                lon: parseFloat(
                  searchState.selectedFilters.longitude as string
                ),
              },
            ],
          },
        };
      }

      let geoContext = {};

      if (searchState.sort && searchState.sort !== 'BEST_MATCH') {
        if (searchState.sort == 'DISTANCE') {
          geoContext = {
            context: {
              geo: {
                location: {
                  lat: parseFloat(
                    searchState.selectedFilters.latitude as string
                  ),
                  lon: parseFloat(
                    searchState.selectedFilters.longitude as string
                  ),
                },
              },
            },
          };

          defaultSearchBody = {
            ...defaultSearchBody,
            sort: {
              value: [
                {
                  name: 'coordinate',
                  order: 'asc',
                },
              ],
            },
          };
        } else {
          const sortValue =
            searchState.sort == 'REVERSE_ALPHABETICAL'
              ? 'locationName_descending'
              : 'locationName_ascending';

          defaultSearchBody = {
            ...defaultSearchBody,
            sort: {
              value: [
                {
                  name: sortValue,
                },
              ],
              choices: false,
            },
          };
        }
      }

      if (query.length > 0) {
        defaultSearchBody = {
          ...defaultSearchBody,
          query: {
            keyphrase: query,
            operator: 'and',
          },
        };
      }

      if (
        Object.keys(nonDefaultSelectedFilters).includes('locationtypes') &&
        nonDefaultSelectedFilters['locationtypes'].length > 0
      ) {
        defaultSearchBody = {
          ...defaultSearchBody,
          facet: {
            all: true,
            max: 100,
            types: typesArray,
          },
        };
      }

      const searchReq = {
        ...geoContext,
        widget: {
          items: [
            {
              rfk_id: getSearchWidget(),
              entity: 'location',
              sources: [getLocationsSearchSrc()],
              search: defaultSearchBody,
            },
          ],
        },
      };
      const response = await Axios.post('/api/discovery', searchReq);

      const data = response.data;

      const locationResults = Array.isArray(data.widgets?.[0]?.content)
        ? data.widgets[0].content
        : [];

      const responseFacets = Array.isArray(data.widgets?.[0]?.facet)
        ? data.widgets[0].facet
        : [];

      const totalCount = data.widgets?.[0]?.total_item ?? 0;

      if (mapRef.current === 'map' && totalCount >= 100) {
        fetchMapResults(locationResults, totalCount, defaultSearchBody);
      } else if (mapRef.current === 'map' && totalCount < 100) {
        mapResultsRef.current = true;
      }

      responseFacets.map((f: FacetType) => {
        if (f.value.length == 100) {
          if (
            JSON.stringify(defaultSearchBody) == JSON.stringify(initialSearch)
          ) {
            getMoreFacets(f);
          } else {
            getMoreFacets(f, defaultSearchBody);
          }
        }
      });

      if (reqNum == currentReq.current) {
        setSearchState(prevState => ({
          ...prevState,
          apiError: false,
          results: locationResults,
          count: totalCount,
          loading: false,
          filters: {
            ...filters,
            languages: responseFacets.find(
              (obj: DataFacet) => obj.name == 'languages'
            )?.value,
            locationtypes: responseFacets.find(
              (obj: DataFacet) => obj.name == 'locationtypes'
            )?.value,
          },
        }));

        document.querySelectorAll('.tenth-item')?.forEach(item => {
          item.classList.remove('showing');
        });
      }
    } catch (error) {
      setSearchState(prevState => ({
        ...prevState,
        apiError: true,
        count: 0,
        loading: false,
        results: [],
      }));
    }
  };

  const getSelectedFilter = (filter: string) => {
    return lowercaseQuery[filter]
      ? Array.isArray(filterDefaults[filter as keyof LocationDefaultsType])
        ? lowercaseQuery[filter]?.toString().split(',')
        : lowercaseQuery[filter]
      : localStorage.getItem(filter)
        ? decodeURIComponent(localStorage.getItem(filter) || '')
        : filterDefaults[filter as keyof LocationDefaultsType];
  };

  const getSelectedFacet = (filter: string) => {
    if (lowercaseQuery[filter] && !localStorage.getItem(filter)) {
      if (lowercaseQuery[filter]?.includes(',')) {
        const values = lowercaseQuery[filter]?.toString().split(',');
        let selectedFacets = Array<FacetValue>();
        values?.forEach(val => {
          selectedFacets.push(
            allFacets
              .filter(facet => facet.name == filter)[0]
              ?.value.filter(fv => fv.text == val)[0]
          );
        });
        localStorage.setItem(filter, JSON.stringify(selectedFacets));
      } else {
        localStorage.setItem(
          filter,
          JSON.stringify(
            allFacets
              .filter(facet => facet.name == filter)[0]
              ?.value.filter(fv => fv.text == lowercaseQuery[filter])
          )
        );
      }
    }

    return localStorage.getItem(filter)
      ? JSON.parse(localStorage.getItem(filter) as string)
      : lowercaseQuery[filter]
        ? allFacets
            .filter(facet => facet.name == filter)[0]
            ?.value.filter(fv => fv.text == lowercaseQuery[filter])
        : filterDefaults[filter as keyof LocationDefaultsType];
  };

  const showResults = () => {
    document.body.classList.add('search-loaded');
  };

  const closeMobileControls = () => {
    document.body.classList.remove('mobile-controls-open');

    setSearchState(prevFields => ({
      ...prevFields,
      mobileControlsOpen: false,
    }));
  };

  const closeDesktopControls = () => {
    document.body.classList.remove('desktop-controls-open');

    setSearchState(prevFields => ({
      ...prevFields,
      desktopControlsOpen: false,
    }));
  };

  const setViewMode = (mode: string) => {
    mapRef.current = mode;

    setSearchState(prevFields => ({
      ...prevFields,
      viewMode: mode,
    }));
    searchType === 'locations' && search();
  };

  const resize = () => {
    const { lastViewportSize, mobileControlsOpen, desktopControlsOpen } =
        searchState,
      viewportSize = getOsfViewportSize();

    if (lastViewportSize !== viewportSize) {
      setSearchState(prevFields => ({
        ...prevFields,
        lastViewportSize: viewportSize,
      }));

      if (lastViewportSize === 'md' && viewportSize === 'lg') {
        mobileControlsOpen && closeMobileControls();
        setViewMode('list');
      }
      if (lastViewportSize === 'lg' && viewportSize === 'md') {
        desktopControlsOpen && closeDesktopControls();
      }
    }
  };

  const updateSort: UpdateSortType = (sort: string) => {
    setSearchState(prevState => ({
      ...prevState,
      sort: sort,
      page: filterDefaults.page,
    }));
  };

  const setDownloadVisability = () => {
    //Shows or hides the modal shown on download button based on number of providers
    const count = searchState.count;
    const downloadBtns = document.querySelectorAll('.download-btn');
    const modalBtns = document.querySelectorAll('.modal-btn');
    if (window.location.search.length <= 1) {
      Array.from(downloadBtns).forEach(downloadBtn => {
        downloadBtn.classList.add('hidden');
      });
      Array.from(modalBtns).forEach(modalBtn => {
        modalBtn.classList.add('hidden');
      });
    } else if (count >= 200) {
      Array.from(downloadBtns).forEach(downloadBtn => {
        downloadBtn.classList.add('hidden');
      });
      Array.from(modalBtns).forEach(modalBtn => {
        modalBtn.classList.remove('hidden');
      });
    } else {
      Array.from(downloadBtns).forEach(downloadBtn => {
        downloadBtn.classList.remove('hidden');
      });
      Array.from(modalBtns).forEach(modalBtn => {
        modalBtn.classList.add('hidden');
      });
    }
  };

  const getResultsCount = () => {
    const { count, pageSize, page, viewMode } = searchState;
    const resultType = 'Locations';
    setDownloadVisability();
    return count === 1 ? (
      <>
        Showing {<strong>{`${count}`}</strong>} {`${resultType}`}
      </>
    ) : pageSize < count && viewMode !== 'map' ? (
      <>
        Showing{' '}
        {
          <strong>
            <span>{`${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, count)} of `}</span>
            {`${count}`}
          </strong>
        }{' '}
        {`${resultType}`}
      </>
    ) : count ? (
      <>
        Showing {<strong>{`${count}`}</strong>} {`${resultType}`}
      </>
    ) : (
      <>
        Showing {<strong>0</strong>} {`${resultType}`}
      </>
    );
  };

  const updateLogoLinks = () => {
    const logoLinks = document.querySelectorAll('.logo-link');
    Array.from(logoLinks).forEach(logoLink => {
      if (logoLink?.getAttribute('href')?.startsWith('/providers')) {
        logoLink.setAttribute('href', `/providers${location.search}`);
      }
    });
  };

  const param = (a: {
    [key: string]: string | Array<string> | number | undefined;
  }) => {
    const s: any[] = [],
      brack = /\[\]$/,
      isArray = function (obj: Object | string) {
        return Object.prototype.toString.call(obj) === '[object Array]';
      },
      add = function (k: string, v: string | number) {
        v = v === null ? '' : v === undefined ? '' : v;
        s[s.length] = encodeURIComponent(k) + '=' + encodeURIComponent(v);
      },
      buildParams = function (prefix: string, obj: any) {
        let i, len, key;

        if (prefix) {
          if (isArray(obj)) {
            for (i = 0, len = obj.length; i < len; i++) {
              if (brack.test(prefix)) {
                add(prefix, obj[i]);
              } else {
                buildParams(
                  prefix + '[' + (typeof obj[i] === 'object' ? i : '') + ']',
                  obj[i]
                );
              }
            }
          } else if (obj && String(obj) === '[object Object]') {
            for (key in obj) {
              buildParams(prefix + '[' + key + ']', obj[key]);
            }
          } else {
            add(prefix, obj);
          }
        } else if (isArray(obj)) {
          for (i = 0, len = obj.length; i < len; i++) {
            add(obj[i].name, obj[i].value);
          }
        } else {
          for (key in obj) {
            buildParams(key, obj[key]);
          }
        }
        return s;
      };
    return buildParams('', a).join('&').replace(/%2C/g, ',');
  };

  const updatePage = (page: number) => {
    setSearchState(state => ({
      ...state,
      page: page,
    }));
  };

  const updateControlPanel: SetControlPanelType = (control: string) => {
    toggleMobileControls(control);
  };

  useEffect(() => {
    //Gets all facets to store in state
    getAllFacets();

    Object.keys(searchState.selectedFacets).forEach(facet => {
      if (
        !lowercaseQuery[facet] ||
        lowercaseQuery[facet] !== localStorage.getItem(facet)
      ) {
        localStorage.removeItem(facet);
      }
    });

    setSearchState(prevFields => ({
      ...prevFields,
      selectedFilters: {
        distance: getSelectedFilter('distance') as string,
        languages: getSelectedFilter('languages') as Array<string>,
        latitude: getSelectedFilter('latitude') as number | undefined,
        location: getSelectedFilter('location') as string | undefined,
        longitude: getSelectedFilter('longitude') as number | undefined,
        locationtypes: getSelectedFilter('locationtypes') as Array<string>,
        PublishToLocal: getSelectedFilter('PublishToLocal') as string,
      },
      query: (lowercaseQuery['query'] as string) || filterDefaults.query,
      queryInputValue:
        (lowercaseQuery['query'] as string) || filterDefaults.query,
      page: parseInt(lowercaseQuery['page'] as string) || filterDefaults.page,
    }));

    const { desktopControlsOpen } = searchState;

    showResults();

    desktopControlsOpen && document.body.classList.add('search-controls-open');

    window.addEventListener('resize', resize);

    if (!lowercaseQuery['latitude'] && !localStorage.getItem('latitude')) {
      geolocate();
    }

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    if (allFacets.length > 0) {
      setSearchState(prevFields => ({
        ...prevFields,
        selectedFacets: {
          locationtypes: getSelectedFacet('locationtypes') as Array<string>,
        },
      }));

      allFacets.forEach(facet => {
        if (facet.value.length == 100 && facet.name == 'locationtypes') {
          getMoreFacets(facet);
        }
      });
    }
  }, [allFacets]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlStateParams = {
        query: searchState.query,
        page: searchState.page === 1 ? null : searchState.page,
        sort: searchState.selectedFilters.location
          ? searchState.sort === 'DISTANCE'
            ? null
            : searchState.sort
          : searchState.sort === filterDefaults.sort
            ? null
            : searchState.sort,
        pageSize:
          searchState.pageSize === pageSizeOptions[0]
            ? null
            : searchState.pageSize,
        ...searchState.selectedFilters,
      };

      let nonDefaultParams: NonDefaultParams = Object.keys(
        urlStateParams
      ).reduce((nonDefaultParams, key) => {
        if (searchState.selectedFilters.location && key !== 'distance') {
          if (
            urlStateParams[key as keyof LocationFiltersType] ===
              filterDefaults[key as keyof LocationDefaultsType] ||
            urlStateParams[key as keyof LocationFiltersType] === null ||
            urlStateParams[key as keyof LocationFiltersType] == ''
          )
            return nonDefaultParams;
        } else if (!searchState.selectedFilters.location) {
          if (
            urlStateParams[key as keyof LocationFiltersType] ===
              filterDefaults[key as keyof LocationDefaultsType] ||
            urlStateParams[key as keyof LocationFiltersType] === null ||
            urlStateParams[key as keyof LocationFiltersType] == ''
          )
            return nonDefaultParams;
        }
        return {
          ...nonDefaultParams,
          [key]: urlStateParams[key as keyof LocationFiltersType]?.toString(),
        };
      }, {});

      //filter out keys that aren't in defaults
      let nonFilterParams: any = Object.fromEntries(
        Object.entries(lowercaseQuery).filter(
          ([key]) =>
            key !== 'path' &&
            !Object.keys(filterDefaults).includes(key) &&
            !Object.keys(urlStateParams).includes(key)
        )
      );

      const qp = { ...nonDefaultParams, ...nonFilterParams };
      const newParam = param(qp);

      const newQuery = { ...lowercaseQuery };
      Object.keys(newQuery).map(key => {
        if (
          key !== 'path' &&
          (nonDefaultParams[key] ==
            filterDefaults[key as keyof LocationDefaultsType] ||
            !nonDefaultParams[key])
        ) {
          delete newQuery[key];
        }
      });

      if (newParam.length > 0) {
        router.push(
          {
            pathname: router.pathname,
            query: { ...newQuery, ...nonDefaultParams, ...nonFilterParams },
          },
          undefined,
          { shallow: true }
        );
      } else {
        router.push(
          {
            pathname: router.pathname,
            query: { ...newQuery, ...nonDefaultParams, ...nonFilterParams },
          },
          undefined,
          { shallow: true }
        );
      }

      if (
        Object.keys(urlStateParams).length !== 0 &&
        !(Object.keys(urlStateParams).length === 1)
      ) {
        urlStateParams.location = searchState.selectedFilters.location;
      }

      searchState.selectedFilters.longitude
        ? localStorage.setItem(
            'longitude',
            searchState.selectedFilters.longitude.toString()
          )
        : delete localStorage.longitude;

      searchState.selectedFilters.latitude
        ? localStorage.setItem(
            'latitude',
            searchState.selectedFilters.latitude.toString()
          )
        : delete localStorage.latitude;

      searchState.selectedFilters.distance
        ? localStorage.setItem(
            'distance',
            searchState.selectedFilters.distance.toString()
          )
        : delete localStorage.distance;

      searchState.selectedFilters.location
        ? localStorage.setItem('location', searchState.selectedFilters.location)
        : delete localStorage.location;

      updateLogoLinks();
    }
    search();
  }, [
    searchState.selectedFilters,
    searchState.selectedFacets,
    searchState.query,
    searchState.sort,
    searchState.page,
  ]);

  useEffect(() => {
    window.location.search
      ? localStorage.setItem('locationSearch', window.location.search)
      : delete localStorage.locationSearch;
  }, [routerQuery]);

  useEffect(() => {
    if (searchState.selectedFilters.location) {
      setSearchState(prevFields => ({
        ...prevFields,
        sort: 'DISTANCE',
      }));
    }
  }, [searchState.selectedFilters.location]);

  useEffect(() => {
    if (mapRef.current === 'map') {
      geolocate();
    }
  }, [searchState.viewMode]);

  const fetchMapResults = async (
    locationResults: Array<LocationFields>,
    totalCount: number,
    defaultSearchBody: any
  ) => {
    const fetches = Math.ceil(totalCount / 100);

    for (let i = 1; i < fetches; i++) {
      defaultSearchBody = {
        ...defaultSearchBody,
        limit: 100,
        offset: i * 100,
        sort: {
          value: [
            {
              name: 'locationName_ascending',
            },
          ],
        },
      };
      try {
        const searchReq = {
          widget: {
            items: [
              {
                rfk_id: getSearchWidget(),
                entity: 'location',
                sources: [getLocationsSearchSrc()],
                search: defaultSearchBody,
              },
            ],
          },
        };
        const response = await Axios.post('/api/discovery', searchReq);
        locationResults.push(...response.data.widgets[0].content);
      } catch (error) {
        console.log('Failed to fetch map results. Error: ' + error);
      }
    }

    mapResultsRef.current = true;
    setSearchState(prevFields => ({
      ...prevFields,
      results: locationResults,
    }));
  };

  return (
    <div className="search-page overhauled">
      <HeroComponent
        rendering={{ uid: 'location-search', componentName: 'HeroComponent' }}
        fields={{
          lowerLevelHero: { value: true },
          BaseImage: {
            value: {
              src: Constants.greenHeroImage,
              width: 4096,
              height: 1755,
            },
          },
          BaseTitle: { value: 'Find a Location' },
          MarginSize: { fields: { heading: { value: '' } } },
        }}
      ></HeroComponent>
      <SearchBar
        clearAllFilters={clearAllFilters}
        filterPanels={searchState.locationFilterPanels}
        setActiveFilterPanel={setActiveFilterPanel}
        activeFilterPanel={searchState.activeFilterPanel}
        filters={searchState.filters}
        selectedFacets={searchState.selectedFacets}
        selectedFilters={searchState.selectedFilters}
        addFilter={addFilter}
        updateFilter={updateFilter}
        replaceFilter={replaceFilter}
        buildAddress={buildAddress}
        geolocate={geolocate}
        geolocating={searchState.geolocating}
        replaceLocationFilters={replaceLocationFilters}
        removeLocationFilters={removeLocationFilters}
        removeFilter={removeFilter}
        setLatLng={setLatLng}
        query={searchState.query}
        queryInputValue={searchState.queryInputValue}
        onQueryInputChange={onQueryInputChange}
        updateQuery={updateQuery}
        closeMobileControls={closeMobileControls}
        resultsCount={searchState.count}
        searchType={searchType}
      ></SearchBar>
      <div className="jss-inner-container">
        <>
          <div className={'search-page-controls locations only-mobile'}>
            <h2 className="screen-recording-only" tabIndex={0}>
              Filters
            </h2>
            <ControlToggles
              selectedFacetsCount={selectedFacetsCount}
              searchType={searchType}
              viewMode={searchState.viewMode}
              onQueryInputChange={onQueryInputChange}
              updateQuery={updateQuery}
              queryInputValue={searchState.queryInputValue}
              sort={searchState.sort}
              updateSort={updateSort}
              withOrigin={searchState.selectedFilters.location ? true : false}
              selectedFilters={searchState.selectedFilters}
              setViewMode={setViewMode}
              updateControlPanel={updateControlPanel}
            />
          </div>
          <div className={`search-page-wrapper locations`}>
            <ResultsControls
              count={searchState.count}
              loading={searchState.loading}
              getResultsCount={getResultsCount}
              updateSort={updateSort}
              activeFilterPanel={searchState.activeFilterPanel}
              removeFilter={removeFilter}
              removeLocationFilters={removeLocationFilters}
              filters={searchState.filters}
              filterDefaults={filterDefaults}
              query={searchState.query}
              updateQuery={updateQuery}
              selectedFilters={searchState.selectedFilters}
              selectedFacets={searchState.selectedFacets}
              selectedFacetsCount={selectedFacetsCount}
              searchType={'locations'}
              sort={searchState.sort}
              withOrigin={searchState.selectedFilters.location ? true : false}
              clearAllFilters={clearAllFilters}
              viewMode={searchState.viewMode}
              setViewMode={setViewMode}
            />
            <div className="left-col">
              <div
                className="bg-overlay"
                onClick={e => {
                  if (searchState.mobileControlsOpen) {
                    const targ = e.target as HTMLElement;
                    !targ.classList.contains('.filter-bar-wrapper') &&
                      !targ.closest('.filter-bar-wrapper') &&
                      updateControlPanel('filters');
                  }
                }}
              >
                <LocationFilters
                  active={searchState.controlPanel === 'filters'}
                  activeFilterPanel={searchState.activeFilterPanel}
                  buildAddress={buildAddress}
                  addFilter={addFilter}
                  clearAllFilters={clearAllFilters}
                  filters={searchState.filters}
                  filterDefaults={filterDefaults}
                  filterPanels={searchState.locationFilterPanels}
                  geolocate={geolocate}
                  geolocating={searchState.geolocating}
                  loading={searchState.loading}
                  selectedFacets={searchState.selectedFacets}
                  selectedFacetsCount={selectedFacetsCount}
                  query={searchState.query}
                  queryInputValue={searchState.queryInputValue}
                  onQueryInputChange={onQueryInputChange}
                  count={searchState.count}
                  removeFilter={removeFilter}
                  replaceFilter={replaceFilter}
                  replaceLocationFilters={replaceLocationFilters}
                  removeLocationFilters={removeLocationFilters}
                  searchType={searchType}
                  selectedFilters={searchState.selectedFilters}
                  setActiveFilterPanel={setActiveFilterPanel}
                  setControlPanel={setControlPanel}
                  setLatLng={setLatLng}
                  updateFilter={updateFilter}
                  updateQuery={updateQuery}
                  desktopControlsOpen={searchState.desktopControlsOpen}
                  closeMobileControls={closeMobileControls}
                  closeDesktopControls={closeDesktopControls}
                  resultsCount={searchState.count}
                />
              </div>
              <div className="search-page-results">
                {searchState.viewMode === 'map' && isMapLoaded ? (
                  <LocationMap
                    results={searchState.results}
                    mapResultsLoaded={mapResultsRef.current}
                    {...(searchState.selectedFilters.longitude &&
                      searchState.selectedFilters.latitude && {
                        calculatedCenter: [
                          searchState.selectedFilters.longitude,
                          searchState.selectedFilters.latitude,
                        ],
                      })}
                  />
                ) : (
                  <LocationsSearchResults
                    apiError={searchState.apiError}
                    clearAllFilters={clearAllFilters}
                    count={searchState.count}
                    origin={{
                      latitude: searchState.selectedFilters.latitude,
                      longitude: searchState.selectedFilters.longitude,
                    }}
                    pageSize={searchState.pageSize}
                    results={searchState.results}
                    searchType={searchType}
                    page={searchState.page}
                    updatePage={updatePage}
                    loading={searchState.loading}
                    viewMode={searchState.viewMode}
                  />
                )}
              </div>
            </div>
            <div id="modal-root" />
          </div>
        </>
      </div>
    </div>
  );
};

export default LocationSearch;
